import { FC } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import type { FinancialHealthStats } from '../../types/financialStats';
import { ChartOverlayWrapper } from './ChartOverlayWrapper';

interface PaymentHistoryChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  _monthsToShow?: number;
  _onExpandClick?: React.MutableRefObject<any>;
}

export const PaymentHistoryChart: FC<PaymentHistoryChartProps> = ({ 
  data, 
  showDebug,
}) => {
  const currentAge = data[data.length - 1]?.age_most_recent_late || 0;
  const hasLatePayments = currentAge > 0;

  const streakOverlay = (
    <div className="absolute right-12 top-2 bg-white rounded-lg border border-slate-200 px-4 py-3">
      <div className="text-sm">
        <div className="text-slate-600">Clean Payment Streak</div>
        <div className="flex items-center gap-1 mt-1">
          <span className="text-amber-400">✨</span>
          <span>All payments on time</span>
        </div>
      </div>
    </div>
  );

  return (
    <ChartOverlayWrapper overlay={streakOverlay}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="paymentHistoryGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1C4583" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#1C4583" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
          <XAxis
            dataKey="month"
            tickFormatter={date => format(parseISO(date), 'MMM yyyy')}
            tickMargin={15}
            angle={-30}
            height={60}
            interval={0}
            textAnchor="end"
            style={{
              fontSize: '12px',
              fontFamily: "'Manrope', sans-serif",
            }}
            stroke="#64748B"
            tickLine={false}
            axisLine={{ stroke: '#E2E8F0' }}
          />
          <YAxis
            tickFormatter={value => `${value}%`}
            domain={[0, 100]}
            tickMargin={10}
            style={{
              fontSize: '12px',
              fontFamily: "'Manrope', sans-serif",
            }}
            stroke="#64748B"
            tickLine={false}
            axisLine={{ stroke: '#E2E8F0' }}
          />
          <Tooltip
            formatter={(value: number) => [value, 'Months Since Late Payment']}
            labelFormatter={label => format(parseISO(label as string), 'MMMM yyyy')}
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          />
          <Area
            type="monotone"
            dataKey="age_most_recent_late"
            stroke="#1C4583"
            fill="url(#paymentHistoryGradient)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, fill: '#1C4583' }}
          />
          {!hasLatePayments && (
            <foreignObject x="0" y="0" width="100%" height="100%">
              <div className="h-full w-full flex flex-col items-center justify-center">
                <div className="text-2xl text-amber-400">✨</div>
                <div className="text-slate-900 text-xl font-medium mt-2">
                  Perfect Payment History
                </div>
                <div className="text-slate-500 text-sm mt-1">No late payments recorded</div>
              </div>
            </foreignObject>
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {showDebug && (
        <div className="absolute top-2 left-2 text-xs text-slate-500">
          Payment history and late payment tracking
        </div>
      )}
    </ChartOverlayWrapper>
  );
};
